import {
  paymentMethod as PaymentMethods,
  paymentType as PaymentTypes,
  paymentMode as PaymentModes,
  policyType as PolicyTypes,
  lookupsJson
} from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { isFarmDirectMonthlyPlan } from 'common/helpers/farm-helpers';
import { filterAutoDirectBillOptions, isUnofferedAutoPlan } from 'core/helpers/auto-direct-bill';

// Shared by the current-term and renewal payment-method selects so the two can't drift. A missing
// key renders an empty select rather than falling back, which stranded escrow-billed FarmHome (Z)
// policies: staff were told to change the payment method to drop the last mortgagee, and couldn't.
export const paymentMethodOptionsMap = {
  [PolicyTypes.Auto]: lookupsJson.autoPaymentMethod,
  [PolicyTypes.Home]: lookupsJson.homeownersPaymentMethod,
  [PolicyTypes.Condo]: lookupsJson.condoPaymentMethod,
  [PolicyTypes.Renters]: lookupsJson.rentersPaymentMethod,
  [PolicyTypes.Farm]: lookupsJson.farmPaymentMethod,
  [PolicyTypes.FarmHome]: lookupsJson.farmPaymentMethod,
  [PolicyTypes.FarmRental]: lookupsJson.farmPaymentMethod
};

/**
 * Payment-method options for the current-term + renewal selects, with Direct-bill ('D') gated per
 * brand for AUTO. Migrated (mutual) auto policies come in as Direct pay, but Branch retail auto
 * doesn't offer Direct — and a stored value with no matching option renders as the raw code "D"
 * (#35123). So Direct is a canonical auto option (it carries the "Direct" label), shown only when the
 * brand enables it via `featureFlags.autoDirectBillPaymentMethod` (on for CFM, off for Branch). Farm
 * already lists Direct for every brand, so only the auto list is filtered. #35123
 */
export const getPaymentMethodOptions = (policyType) => {
  const options = paymentMethodOptionsMap[policyType] || [];
  return policyType === PolicyTypes.Auto ? filterAutoDirectBillOptions(options) : options;
};

/**
 * Frequency choices allowed for the selected payment method — shared by the current-term and renewal
 * selects. Mortgage is Escrow only, Bill to other is One Time only, Direct is never Monthly (the same
 * rules the offer Settings tab applies, #36200). `mortgageMethod` lets the renewal select key the
 * Escrow rule off the renewal method while the other rules follow the current-term method.
 *
 * Exceptions: a plan that is already Direct + Monthly keeps Monthly in the list so the select shows the
 * saved plan instead of a blank frequency, which staff misread as pay-in-full (#37291).
 * - CFM auto (a migrated "Direct Bill 6 Pay", which the mutuals don't sell): the tab flags it as
 *   needing a change.
 * - Farm (migrated Forreston payers, see isFarmDirectMonthlyPlan): the plan stays, and the tab points
 *   staff at moving the member to automatic payments. This keys off `savedPlan`, the plan stored on the
 *   policy, not the form: an ACH + Monthly policy edited to Direct keeps Monthly through
 *   getNormalizedBillingDefaults, and must not be offered a plan only migrated policies may keep.
 */
export const filterFrequencyOptionsForMethod = ({
  options = [],
  paymentMethod,
  mortgageMethod = paymentMethod,
  currentPaymentType,
  savedPlan,
  policyType
}) =>
  options.filter((opt) => {
    if (mortgageMethod === PaymentMethods.Escrow) {
      return opt.id === PaymentTypes.Escrow;
    }
    if (paymentMethod === PaymentMethods.BillToOther) {
      return opt.id === PaymentTypes.OneTime;
    }
    if (paymentMethod === PaymentMethods.Direct) {
      const currentPlan = { method: paymentMethod, type: currentPaymentType };
      const keepsDirectMonthly =
        (policyType === PolicyTypes.Auto && isUnofferedAutoPlan(currentPlan)) ||
        (isFarmBasedPolicyType(policyType) && Boolean(savedPlan) && isFarmDirectMonthlyPlan(savedPlan));
      if (opt.id === PaymentTypes.Monthly && keepsDirectMonthly) {
        return true;
      }
      return ![PaymentTypes.Escrow, PaymentTypes.Monthly].includes(opt.id);
    }
    return true;
  });

export const daysOfMonth = Array.from({ length: 28 }, (_, day) => ({
  id: day + 1,
  value: `${day + 1}`
}));

export const maskingDots = '\u25CF\u25CF\u25CF\u25CF';

// Single source of truth for how each payment method constrains paymentType/paymentMode. Two callers
// share it so the rules can't drift: the payment-method sub-components enforce it on mount (via
// useNormalizePaymentMethod), and the Payment tab seeds its Formik initialValues from it so the form
// doesn't open pre-dirtied — the on-mount pass then finds nothing to change and the "Save changes"
// footer only shows on real edits (#32985).
export const getNormalizedBillingDefaults = ({ paymentMethod, paymentType, paymentMode, policyType }) => {
  // When paymentMethod is unset, mirror billing-details-form's `values.paymentMethod || defaultPaymentMethod`
  // render fallback so the seed matches whichever method component actually mounts.
  const effectiveMethod =
    paymentMethod || (policyType === PolicyTypes.Auto ? PaymentMethods.CreditCard : PaymentMethods.Escrow);

  switch (effectiveMethod) {
    case PaymentMethods.ACH:
      return {
        paymentType: paymentType === PaymentTypes.Escrow ? PaymentTypes.OneTime : paymentType,
        paymentMode: PaymentModes.Automated
      };
    case PaymentMethods.CreditCard:
      return { paymentType, paymentMode: PaymentModes.Automated };
    case PaymentMethods.Escrow:
      return { paymentType: PaymentTypes.Escrow, paymentMode: PaymentModes.Automated };
    case PaymentMethods.Direct:
      return { paymentType, paymentMode: PaymentModes.Direct };
    case PaymentMethods.BillToOther:
      return { paymentType: PaymentTypes.OneTime, paymentMode: PaymentModes.Direct };
    default:
      return { paymentType, paymentMode };
  }
};
